import React, { useCallback, useEffect, useState, VFC } from 'react';
import { Organization } from 'src/common/interfaces/organization.interface';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import { OrganizationInvitedUser } from 'src/common/interfaces/organization/invited-user.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import {
  apiDeleteOrganizationInvite,
  apiGetOrganizationInvites,
  apiGetOrganizationUsers,
  apiResendOrganizationInvite,
  apiRevokeUser,
} from 'src/utils/journeyApi';
import { DashboardSettingsMembersSection } from './section';
import { EventBus } from 'src/common/EventBus';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { getInviteLink } from 'src/utils/get-invite-link';

interface DashboardSettingsMembersProps {
  currentOrganization: Organization;
}

export type OrganizationMemberType = Record<string, string | number | boolean>;

export const DashboardSettingsMembers: VFC<DashboardSettingsMembersProps> = ({ currentOrganization }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [members, setMembers] = useState<OrganizationMemberType[]>([]);

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const createTableRow = useCallback(
    (user: OrganizationUser | OrganizationInvitedUser, isMember: boolean = false) => ({
      name: user.name || user.email,
      email: user.email,
      isMember: isMember,
      id: (user as OrganizationInvitedUser).uuid || (user as OrganizationUser).id,
    }),
    []
  );

  useEffect(() => {
    const fetchOrganizationInvites = () => apiGetOrganizationInvites(currentOrganization.id);
    const fetchOrganizationUsers = () => apiGetOrganizationUsers(currentOrganization.id);
    Promise.all([fetchOrganizationInvites(), fetchOrganizationUsers()])
      .then(([{ invites }, { users }]) => {
        const sortedMembers = [
          ...invites.map((i: OrganizationInvitedUser) => createTableRow(i, false)),
          ...users.map((u: OrganizationUser) => createTableRow(u, true)),
        ].sort((u1, u2) => u2.isMember - u1.isMember);
        setMembers(sortedMembers);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setErrorNotification((err as ErrorInterface).message);
      });
    // giving dependencies causes fetching data twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onUserRevoked = async (userId: string) => {
      try {
        await apiRevokeUser(currentOrganization.id, userId);
        setSuccessNotification('User revoked');
        setMembers(members.filter((member) => member.id !== parseInt(userId)));
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    };

    const onMemberInvited = (invitedUser: OrganizationInvitedUser | OrganizationUser) => {
      if ((invitedUser as OrganizationInvitedUser).created_by_user) {
        // check if invitedUser is a OrganizationUser or OrganizationInvitedUser
        setMembers([...members, createTableRow(invitedUser, false)]);
      } else {
        // otherwise it's a OrganizationUser
        setMembers([...members, createTableRow(invitedUser, true)]);
      }

      setSuccessNotification('New member invited');
      
      // Emit event for onboarding checklist
      EventBus.emit('onboarding:team-invited');
    };

    const onMemberInviteFailed = (message: string) => {
      setErrorNotification(message);
    };

    const onInvitationRemoved = async (uuid: string) => {
      try {
        await apiDeleteOrganizationInvite(currentOrganization.id, uuid);
        setMembers([...members.filter((member) => member.id !== uuid)]);
        setSuccessNotification('Invitation removed');
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    };

    const onInvitationLinkCopied = async (uuid: string) => {
      await navigator.clipboard.writeText(getInviteLink(uuid));
      setSuccessNotification('Invitation link copied');
    };

    const onResendInvitation = async (uuid: string) => {
      try {
        await apiResendOrganizationInvite(currentOrganization.id, uuid);
        setSuccessNotification('Invitation resent');
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    };

    EventBus.on('dashboard-settings:copy-invitation-url', onInvitationLinkCopied);
    EventBus.on('dashboard-settings:resend-invitation', onResendInvitation);
    EventBus.on('dashboard-settings:remove-invitation', onInvitationRemoved);
    EventBus.on('dashboard-settings:success-invite-member', onMemberInvited);
    EventBus.on('dashboard-settings:fail-invite-member', onMemberInviteFailed);
    EventBus.on('dashboard-settings:revoke-user', onUserRevoked);

    return () => {
      EventBus.off('dashboard-settings:copy-invitation-url', onInvitationLinkCopied);
      EventBus.off('dashboard-settings:resend-invitation', onResendInvitation);
      EventBus.off('dashboard-settings:remove-invitation', onInvitationRemoved);
      EventBus.off('dashboard-settings:success-invite-member', onMemberInvited);
      EventBus.off('dashboard-settings:fail-invite-member', onMemberInviteFailed);
      EventBus.off('dashboard-settings:revoke-user', onUserRevoked);
    };
  }, [createTableRow, currentOrganization.id, members, setErrorNotification, setSuccessNotification]);

  return (
    <DashboardSettingsMembersSection
      loading={isLoading}
      currentOrganizationId={currentOrganization.id}
      members={members}
    />
  );
};
